import { useCallback, useEffect, useMemo, useState } from 'react';
import type { SelectOptionObject } from '@looker/components';
import { keyBy } from 'lodash';

import { FieldSelectMulti } from '@/components/looker/field-select-multi';
import { useMe } from '@/hooks/use-me';

import { ListUnits } from '../api/queries/list-units';
import type { Top } from '../entities/top';
import type { Unit } from '../entities/unit';

export type UnitsFilterProps = {
  hide?: boolean;
  top?: Top;
  value: Unit[];
  onChange: (value: Unit[]) => void;
  deviceId?: string;
};

export function UnitsFilter({
  hide = false,
  top,
  value,
  onChange,
  deviceId,
}: UnitsFilterProps) {
  const me = useMe();
  const listUnits = ListUnits.useQuery({ top }, { retry: 3 });

  const unitOptions = useMemo<SelectOptionObject[]>(
    () =>
      listUnits.data?.units
        .map((unit) => ({ label: unit.label, value: unit.id }))
        .sort((a, b) => a.label.localeCompare(b.label)) ?? [],
    [listUnits.data],
  );

  const unitMap = useMemo<Record<string, Unit>>(
    () => keyBy(listUnits.data?.units ?? [], 'id'),
    [listUnits.data?.units],
  );

  const label = useMemo(() => {
    return me.config.sitesWithDifferentLevels
      ? 'Unit Level ID'
      : me.config.unit.labels.id;
  }, [me.config]);

  // Hold unpersisted selection until the dropdown closes, then commit
  const [unpersistedIds, setUnpersistedIds] = useState<string[] | undefined>(
    undefined,
  );

  const selectFirstUnit = useCallback(() => {
    if (listUnits.data?.units?.length) {
      const sortedUnits = listUnits.data.units.sort((a, b) =>
        a.label.localeCompare(b.label),
      );
      onChange([sortedUnits[0]]);
      return;
    }
  }, [listUnits.data?.units, onChange]);

  useEffect(() => {
    // Only continue if no value is selected and the list of units is loaded
    if (value.length || !listUnits.isSuccess) {
      return;
    }

    // Don't auto-select if there's a device being processed (let device processing handle it)
    if (deviceId) {
      return;
    }

    // Set the first unit as the default (device processing is handled at hook level)
    selectFirstUnit();
  }, [value.length, selectFirstUnit, listUnits.isSuccess, deviceId]);

  if (hide) {
    return null;
  }

  return (
    <FieldSelectMulti
      data-testid="units-filter"
      className="max-w-[250px]"
      key={top?.id} // INFO: force the refresh of the values
      label={label}
      placeholder={
        (unpersistedIds ?? value)?.length ? undefined : 'Select\u2026'
      }
      options={unitOptions}
      isLoading={listUnits.isLoading && listUnits.fetchStatus !== 'idle'}
      values={unpersistedIds ?? value.map((unit) => unit.id)}
      onChange={(ids: string[] = []) => setUnpersistedIds(ids)}
      onClose={() => {
        if (!unpersistedIds) return;
        onChange(unpersistedIds.map((id) => unitMap[id]));
        setUnpersistedIds(undefined);
      }}
      limit={5}
      autoResize={false}
    />
  );
}
